import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileUp, Upload, Loader2, FileText, Image, CheckCircle2, 
  AlertTriangle, Trash2, GripVertical, Plus, Eye, Save
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PDFImportModal({ open, onClose, auth, models, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload, review, select-model
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const resetState = () => {
    setStep('upload');
    setUploading(false);
    setSaving(false);
    setDraftData(null);
    setSelectedModelId('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Seleziona un file PDF");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File troppo grande (max 50MB)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/admin/import-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.token}`
        },
        body: formData
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Errore nell'importazione");
      }

      setDraftData(data);
      setStep('review');
      toast.success("PDF importato! Rivedi il contenuto.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const updateStep = (index, field, value) => {
    setDraftData(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const addStep = () => {
    setDraftData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          title: `Passo ${prev.steps.length + 1}`,
          content: "",
          type: "normal",
          images: []
        }
      ]
    }));
  };

  const removeStep = (index) => {
    if (draftData.steps.length <= 1) {
      toast.error("Devi avere almeno un passo");
      return;
    }
    setDraftData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const handlePublish = async () => {
    if (!selectedModelId) {
      toast.error("Seleziona un'opzione");
      return;
    }

    setSaving(true);
    try {
      // First update the draft with any changes
      const updateRes = await fetch(`${API}/admin/import-pdf/drafts/${draftData.draft_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          title: draftData.title,
          intro: draftData.intro,
          steps: draftData.steps,
          is_ocr: draftData.is_ocr,
          source_filename: draftData.source_filename
        })
      });

      if (!updateRes.ok) {
        const errData = await updateRes.json();
        throw new Error(errData.detail || "Errore nell'aggiornamento della bozza");
      }

      // Build publish URL with appropriate parameters
      let publishUrl = `${API}/admin/import-pdf/drafts/${draftData.draft_id}/publish`;
      
      if (selectedModelId === 'new') {
        // Create new model
        publishUrl += `?create_new_model=true&new_model_name=${encodeURIComponent(draftData.title)}`;
      } else {
        // Use existing model
        publishUrl += `?model_id=${selectedModelId}`;
      }

      // Publish the guide
      const publishRes = await fetch(publishUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.token}`
        }
      });

      const publishData = await publishRes.json();
      
      if (!publishRes.ok) {
        throw new Error(publishData.detail || "Errore nella pubblicazione");
      }

      toast.success("Guida creata con successo!");
      handleClose();
      if (onSuccess) onSuccess(publishData.model_id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Available models (without existing guides)
  const availableModels = models.filter(m => !m.guide);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            {step === 'upload' && "Importa da PDF"}
            {step === 'review' && "Revisiona Contenuto"}
            {step === 'select-model' && "Seleziona Modello"}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && "Carica un PDF per convertirlo automaticamente in una guida step-by-step"}
            {step === 'review' && "Modifica titolo, introduzione e passi prima di pubblicare"}
            {step === 'select-model' && "Scegli a quale modello associare questa guida"}
          </DialogDescription>
        </DialogHeader>

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="flex-1 py-8">
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                  <p className="text-lg font-medium">Elaborazione PDF in corso...</p>
                  <p className="text-sm text-muted-foreground">Estrazione testo e immagini</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">
                    Trascina qui il tuo PDF
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    oppure clicca per selezionare un file
                  </p>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileInput}
                    className="hidden"
                    id="pdf-input"
                  />
                  <Button asChild>
                    <label htmlFor="pdf-input" className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Seleziona PDF
                    </label>
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Supporta PDF testuali e scansioni (con OCR). Max 50MB.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Review Step */}
        {step === 'review' && draftData && (
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6 py-4">
              {/* OCR Warning */}
              {draftData.is_ocr && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-amber-800 dark:text-amber-200">
                    Questo PDF è stato elaborato con OCR. Verifica attentamente il testo estratto.
                  </span>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label>Titolo Guida</Label>
                <Input
                  value={draftData.title}
                  onChange={(e) => setDraftData({ ...draftData, title: e.target.value })}
                  placeholder="Titolo della guida"
                />
              </div>

              {/* Intro */}
              <div className="space-y-2">
                <Label>Introduzione (opzionale)</Label>
                <Textarea
                  value={draftData.intro}
                  onChange={(e) => setDraftData({ ...draftData, intro: e.target.value })}
                  placeholder="Breve introduzione alla guida..."
                  rows={3}
                />
              </div>

              {/* Steps */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Passi ({draftData.steps.length})</Label>
                  <Button variant="outline" size="sm" onClick={addStep}>
                    <Plus className="w-4 h-4 mr-1" />
                    Aggiungi passo
                  </Button>
                </div>

                <div className="space-y-3">
                  {draftData.steps.map((stepItem, index) => (
                    <Card key={index} className="relative">
                      <CardHeader className="pb-2">
                        <div className="flex items-start gap-2">
                          <Badge variant="secondary" className="shrink-0">
                            {index + 1}
                          </Badge>
                          <Input
                            value={stepItem.title}
                            onChange={(e) => updateStep(index, 'title', e.target.value)}
                            placeholder="Titolo del passo"
                            className="font-medium"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-destructive hover:text-destructive"
                            onClick={() => removeStep(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Textarea
                          value={stepItem.content}
                          onChange={(e) => updateStep(index, 'content', e.target.value)}
                          placeholder="Contenuto del passo..."
                          rows={3}
                        />
                        {stepItem.images && stepItem.images.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {stepItem.images.map((img, imgIdx) => (
                              <div key={imgIdx} className="relative">
                                <img
                                  src={img}
                                  alt={`Immagine ${imgIdx + 1}`}
                                  className="w-20 h-20 object-cover rounded-lg border"
                                />
                                <Badge className="absolute -top-2 -right-2 w-5 h-5 p-0 flex items-center justify-center">
                                  <Image className="w-3 h-3" />
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        )}

        {/* Select Model Step */}
        {step === 'select-model' && (
          <div className="flex-1 py-4">
            <div className="space-y-4">
              {/* Option to create NEW model */}
              <div className="space-y-3">
                <Label>Crea un nuovo modello per questa guida:</Label>
                <Card
                  className={`cursor-pointer transition-all ${
                    selectedModelId === 'new'
                      ? 'ring-2 ring-primary bg-primary/5'
                      : 'hover:bg-muted/50 border-dashed'
                  }`}
                  onClick={() => setSelectedModelId('new')}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedModelId === 'new' ? 'bg-primary text-white' : 'bg-muted'
                    }`}>
                      <Plus className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">+ Crea Nuovo Modello</p>
                      <p className="text-sm text-muted-foreground">
                        Verrà creato automaticamente un modello con il titolo della guida
                      </p>
                    </div>
                    {selectedModelId === 'new' && (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Existing models without guides */}
              {availableModels.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-muted-foreground">Oppure seleziona un modello esistente:</Label>
                  <div className="grid gap-2">
                    {availableModels.map((model) => (
                      <Card
                        key={model.id}
                        className={`cursor-pointer transition-all ${
                          selectedModelId === model.id
                            ? 'ring-2 ring-primary bg-primary/5'
                            : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setSelectedModelId(model.id)}
                      >
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            selectedModelId === model.id ? 'bg-primary text-white' : 'bg-muted'
                          }`}>
                            {selectedModelId === model.id ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{model.name}</p>
                            <p className="text-xs text-muted-foreground">{model.code}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              Annulla
            </Button>
          )}

          {step === 'review' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>
                Indietro
              </Button>
              <Button onClick={() => setStep('select-model')}>
                Continua
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {step === 'select-model' && (
            <>
              <Button variant="outline" onClick={() => setStep('review')}>
                Indietro
              </Button>
              <Button 
                onClick={handlePublish} 
                disabled={!selectedModelId || saving}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                Crea Guida
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
